import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Select, Tag, Space, Tabs } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import thLocale from '@fullcalendar/core/locales/th'; // Import Thai locale
import withRoleAccess from '../../hoc/withRoleAccess';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import CustomModal from './CustomModal';
import moment from 'moment-timezone';
import AllClassList from "./AllClassList";
import { confirmAlert } from 'react-confirm-alert'; // Import the confirmation dialog
import 'react-confirm-alert/src/react-confirm-alert.css'; // Import the styles
import 'antd/dist/reset.css';
import './Classes.css';
import ClassDetailsModal from './ClassDetailsModal';

const { Option } = Select;
const { TabPane } = Tabs;

function AllClasses() {
  const { t, i18n } = useTranslation();
  const [classes, setClasses] = useState([]);
  const [teacherEvents, setTeacherEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [currentEventInfo, setCurrentEventInfo] = useState(null);
  const [changeReason, setChangeReason] = useState('');
  const [changeNote, setChangeNote] = useState('');
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL;



  const parseScheduleHour = (scheduleHourString) => {
    const [hours, minutes] = scheduleHourString.split(' ').map((value, index) => (index === 0 ? parseInt(value) * 60 : parseInt(value)));
    return hours + minutes;
  };

  const tagRender = (props) => {
    const { label, value, closable, onClose } = props;
    const teacher = teachers.find(t => t.userid === value);
    
    return (
      <Tag
        color={teacher?.color || '#108ee9'}
        closable={closable}
        onClose={onClose}
        style={{ 
          marginRight: 3,
          display: 'flex',
          alignItems: 'center',
          padding: '4px 8px',
        }}
      >
        <Space size={4}>
          <UserOutlined style={{ fontSize: '12px' }} />
          {label}
        </Space>
      </Tag>
    );
  };

  const fetchClasses = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError("No token found");
        setLoading(false);
        return;
      }
      const response = await axios.get(`${API_URL}/classes`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setClasses(response.data);
    } catch (error) {
      console.error('Error fetching classes:', error);
      setError('Failed to fetch classes. Please ensure you are logged in.');
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

 
  const fetchTeacherEvents = useCallback(async (teacherIds) => {
    if (teacherIds.length === 0) {
      setTeacherEvents([]);
      return;
    }
  
    try {
      const token = localStorage.getItem('token');
      const promises = teacherIds.map((teacherId) =>
        axios.get(`${API_URL}/teachers/calendar/${teacherId}/events-with-shared`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then(response => ({ 
          teacherId,  // Include teacherId with the response
          data: response.data 
        }))
      );
  
      const results = await Promise.all(promises);
      
      // Format events once when fetching
      const formattedEvents = results.flatMap((result) => 
        result.data.map(event => {
          const teacher = teachers.find(t => t.userid === result.teacherId); // Use the passed teacherId
          const isShared = event.is_shared || false;
  
          return {
            ...event,
            id: `event-${event.eventid}`,
            start: moment.utc(event.start).local().format(),
            end: moment.utc(event.end).local().format(),
            color: isShared ? '#005082' : (teacher ? teacher.color : '#444444'),
            backgroundColor: isShared 
              ? '#00508260' 
              : (teacher ? `${teacher.color}60` : '#44444480'),
            borderColor: isShared ? '#005082' : (teacher ? teacher.color : '#444444'),
            classNames: ['teacher-schedule-event']
          };
        })
      );
  
      setTeacherEvents(formattedEvents);
    } catch (error) {
      console.error('Error fetching teacher events:', error);
      setError('Failed to fetch events. Please try again later.');
    }
  }, [API_URL, teachers]);
  
  useEffect(() => {
    fetchClasses();
    setTeacherEvents([]); 
  }, [fetchClasses, fetchTeacherEvents]);
  

useEffect(() => {
  const fetchTeachers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/teachers`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Correctly access the teachers array
      const activeTeachers = response.data.teachers.filter((teacher) => teacher.status === 'active');

      const teachersWithColors = activeTeachers.map((teacher) => ({
        ...teacher,
        color: teacher.color || '#000000',
      }));

      setTeachers(teachersWithColors);

      // Debug: console.log(teachersWithColors);
    } catch (error) {
      console.error('Error fetching teachers:', error);
    }
  };

  fetchTeachers();
}, [API_URL]);

  

  useEffect(() => {
    const fetchClassrooms = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/classrooms`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
  
        const classroomsData = response.data || [];
  
        // Filter classrooms with availability 'true'
        const availableClassrooms = classroomsData.filter(classroom => classroom.availability === true);
  
        // Sort by 'number' field in alphanumeric order
        const sortedClassrooms = availableClassrooms.sort((a, b) => {
          return a.number.localeCompare(b.number, undefined, { numeric: true, sensitivity: 'base' });
        });
  
        setClassrooms(sortedClassrooms);
      } catch (error) {
        console.error('Error fetching classrooms:', error);
      }
    };
  
    fetchClassrooms();
  }, [API_URL]);
  

  useEffect(() => {
    if (selectedTeachers.length > 0) {
      fetchTeacherEvents(selectedTeachers);
    } else {
      setTeacherEvents([]);
    }
  }, [selectedTeachers, fetchTeacherEvents]);



  const checkOverlap = (newStart, newEnd, teacherId, eventId, allClasses, teacherEvents) => {
    return [...allClasses, ...teacherEvents].some(event => {
      // Skip the current event being moved/resized
      if (event.classid?.toString() === eventId || event.eventid?.toString() === eventId) return false;
      
      // Check all events for the same teacher
      if (event.teacher_id !== teacherId && event.userid !== teacherId) return false;
  
      const eventStart = moment(event.start || `${event.date} ${event.schedule_time}`);
      const eventEnd = moment(event.end || `${event.date} ${event.end_time}`);
  
      return (
        newStart.isBetween(eventStart, eventEnd, null, '[)') ||
        newEnd.isBetween(eventStart, eventEnd, null, '(]') ||
        (newStart.isSameOrBefore(eventStart) && newEnd.isSameOrAfter(eventEnd))
      );
    });
  };

  const handleEventDrop = (info) => {
    const eventId = info.event.id;
    const classItem = classes.find(cls => cls.classid.toString() === eventId);
  
    if (classItem && classItem.status === 'approved') {
      confirmAlert({
        title: t('Action Not Allowed'),
        message: t('You cannot change an approved class.'),
        buttons: [{ label: t('ok'), onClick: () => info.revert() }],
      });
      return;
    }
  
    const newStart = moment(info.event.start);
    const newEnd = moment(info.event.end);
    const teacherId = classItem?.teacher_id;
  
    const isOverlapping = checkOverlap(newStart, newEnd, teacherId, eventId, classes, teacherEvents);
  
    if (isOverlapping) {
      confirmAlert({
        title: t('Time Slot Conflict'),
        message: t('The selected time overlaps with an existing class or event for the same teacher. Please choose a different time.'),
        buttons: [{ label: t('Ok'), onClick: () => info.revert() }],
      });
      return;
    }
  
    setCurrentEventInfo(info);
    setShowModal(true);
  };
  
  const handleEventResize = (info) => {
    const eventId = info.event.id;
    const classItem = classes.find(cls => cls.classid.toString() === eventId);
  
    if (classItem && classItem.status === 'approved') {
      confirmAlert({
        title: t('Action Not Allowed'),
        message: t('You cannot change an approved class.'),
        buttons: [{ label: t('ok'), onClick: () => info.revert() }],
      });
      return;
    }
  
    const newStart = moment(info.event.start);
    const newEnd = moment(info.event.end);
    const teacherId = classItem?.teacher_id;
  
    const isOverlapping = checkOverlap(newStart, newEnd, teacherId, eventId, classes, teacherEvents);
  
    if (isOverlapping) {
      confirmAlert({
        title: t('Time Slot Conflict'),
        message: t('The selected time overlaps with an existing class or event for the same teacher. Please choose a different time.'),
        buttons: [{ label: t('Ok'), onClick: () => info.revert() }],
      });
      return;
    }
  
    setCurrentEventInfo(info);
    setShowModal(true);
  };

  const handleSubmitModal = async () => {
    const { event } = currentEventInfo;
    const classItem = classes.find(cls => cls.classid.toString() === event.id);

    // Define original and new times
    const originalStartTime = classItem.schedule_time; 
    const originalEndTime = classItem.end_time; 
    const newStartTime = event.start.toTimeString().split(' ')[0].substring(0, 5);
    const newEndTime = event.end.toTimeString().split(' ')[0].substring(0, 5);

    // Format dates with moment-timezone
    const originalDate = moment.tz(classItem.date, 'Asia/Bangkok').format('YYYY-MM-DD');
    const newDate = moment.tz(event.start, 'Asia/Bangkok').format('YYYY-MM-DD');

    const updatedEvent = {
        id: event.id,
        date: newDate,
        schedule_time: newStartTime,
        schedule_hour: ((event.end - event.start) / 60000).toFixed(0)
    };

    try {
        const token = localStorage.getItem('token');
        await axios.put(`${API_URL}/classes/${updatedEvent.id}`, updatedEvent, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        });

        await axios.post(`${API_URL}/class-change/class-change-logs`, {
            classid: updatedEvent.id,
            teacherid: classItem.teacher_id,
            originaldate: originalDate,
            originalstarttime: originalStartTime,
            originalendtime: originalEndTime,
            newdate: newDate,
            newstarttime: newStartTime,
            newendtime: newEndTime,
            changereason: changeReason,
            note: changeNote,
            updateby: token // Use token to determine the updating user
        }, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        });

        setShowModal(false);
      fetchClasses(); // Refresh the class list after update
    } catch (error) {
      console.error('Error updating event:', error);
      setShowModal(false);
      setCurrentEventInfo(null);
    }
  }; 

  const handleEventClick = (info) => {
    const eventId = info.event.id;
    
    // For class events (not teacher events)
    if (!eventId.includes('event-')) {
      const classItem = classes.find(cls => cls.classid.toString() === eventId);
      if (classItem) {
        const teacher = teachers.find(t => t.userid === classItem.teacher_id);
        setSelectedClass({
          classData: classItem,
          teacherData: teacher
        });
        setDetailsModalVisible(true);
      }
    } else {
      // For teacher events - you can handle these differently or ignore them
      const eventIdNum = eventId.replace('event-', '');
      const teacherEvent = teacherEvents.find(evt => evt.eventid?.toString() === eventIdNum);
      if (teacherEvent) {
        // You can choose to display teacher events in a different way or not show details
        console.log('Teacher event clicked', teacherEvent);
      }
    }
  };
  
// Filter classes based on search term and teacher selection
const filteredClasses = classes
  .filter((classItem) => {
    const isSearchMatch =
      classItem.subject_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      classItem.class_code?.toLowerCase().includes(searchTerm.toLowerCase());
    const isTeacherMatch =
      selectedTeachers.length > 0
        ? selectedTeachers.includes(classItem.teacher_id)
        : true;
    return isSearchMatch && isTeacherMatch;
  })
  .sort((a, b) => {
    // First, compare dates
    const dateComparison = new Date(a.date) - new Date(b.date);
    if (dateComparison !== 0) {
      return dateComparison;
    }
    // If dates are the same, compare schedule times
    return moment(a.schedule_time, "HH:mm:ss").diff(
      moment(b.schedule_time, "HH:mm:ss")
    );
  });



// Separate future and past classes
const today = new Date().setHours(0, 0, 0, 0);
const pastClasses = filteredClasses.filter((classItem) => new Date(classItem.date) < today);


// Calculate total pages
const rowsPerPage = 55;

// Set the initial page to where today or future classes start
useEffect(() => {
  const firstFutureIndex = pastClasses.length;
  const initialPage = Math.ceil((firstFutureIndex + 1) / rowsPerPage);
  setCurrentPage(initialPage > 1 ? initialPage : 1);
}, [pastClasses.length]);



  const formatClassToEvent = (classItem) => {
    const startDate = new Date(classItem.date);
    const [startHours, startMinutes] = classItem.schedule_time.split(':').map(Number);

    if (isNaN(startHours) || isNaN(startMinutes)) {
        console.error('Invalid schedule time format:', classItem.schedule_time);
        return null;
    }

    startDate.setHours(startHours);
    startDate.setMinutes(startMinutes);
    startDate.setSeconds(0);
    startDate.setMilliseconds(0);

    if (isNaN(startDate.getTime())) {
        console.error('Invalid start date or time:', classItem.date, classItem.schedule_time, startDate);
        return null;
    }

    const totalMinutes = parseScheduleHour(classItem.schedule_hour || '');
    const endDateTime = new Date(startDate.getTime() + totalMinutes * 60000);

    if (isNaN(endDateTime.getTime())) {
        console.error('Invalid end date or time:', startDate, endDateTime, classItem.schedule_hour);
        return null;
    }

    const teacher = teachers.find((t) => t.userid === classItem.teacher_id);

    const titleLines = [
      `${classItem.class_code}`,
      `${classItem.subject_name} (${classItem.classid})`,
      `${classItem.nickname} / Room: ${classItem.classroom_number || '-'}`
  ];

    return {
        id: classItem.classid.toString(),  
        title: titleLines.join('<br />'), 
        start: startDate.toISOString(), 
        end: endDateTime.toISOString(),
        color: teacher ? teacher.color : '#333333', 
        backgroundColor: teacher ? teacher.color : '#333333',
    };
};

const renderEventContent = (arg) => {
  const event = arg.event;
  const startTime = event.start
    ? moment(event.start).format('HH:mm')
    : '';
  const endTime = event.end
    ? moment(event.end).format('HH:mm')
    : '';
  
  return {
    html: `
      <div class="calendar-event teacher-event" style="
        padding: 4px;
        border-radius: 4px;
        opacity: 0.8;
        min-height: 100%;
        font-size: 0.85em;
      ">
        <div class="event-time">${startTime} - ${endTime}</div>
        <div class="event-title" style="white-space:pre-line">${event.title}</div>
      </div>
    `,
  };
};

const calendarEvents = [
  ...filteredClasses.map(formatClassToEvent).filter((event) => event !== null),
  ...teacherEvents // Use directly since they're already formatted
];

  if (loading) return <p>Loading...</p>;
  if (error) return <p>{error}</p>;



  return (
    <div className="class-container">
      <Sidebar />
      <Header />
      <Tabs defaultActiveKey="1" size="large">
      <TabPane tab="Calendar" key="1">
      <div className="top-filter-container">
      <div className="teacher-select-container">
      <Select
  mode="multiple"
  showSearch
  style={{ width: '100%' }}
  placeholder={t('Select Teachers')}
  value={selectedTeachers}
  onChange={setSelectedTeachers}
  optionFilterProp="label" // Changed from children to label
  tagRender={tagRender}
  maxTagCount={3}
  maxTagPlaceholder={(omittedValues) => `+ ${omittedValues.length} ${t('more teachers')}`}
  filterOption={(input, option) => {
    // Check if the search term matches nickname or firstname
    return option?.label?.toLowerCase().includes(input.toLowerCase());
  }}
  dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
>
  {teachers.map(teacher => (
    <Option 
      key={teacher.userid} 
      value={teacher.userid}
      label={`${teacher.nickname} ${teacher.firstname}`} // Add label prop for search
      style={{
        padding: '8px 12px',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Space>
        <Tag color={teacher.color} style={{ margin: 0, width: 8, height: 8 }} />
        <span>{teacher.nickname} {teacher.firstname}</span>
      </Space>
    </Option>
  ))}
</Select>
      </div>
      </div>

    <div className="class-calendar-container">
      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        allDaySlot={false}
        locale={i18n.language === 'th' ? thLocale : 'en'}
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay',
        }}
        slotMinTime="10:00:00"
        slotMaxTime="20:00:00"
        slotDuration="00:15:00"
        snapDuration="00:15:00" 
        events={calendarEvents}
        editable={true}
        droppable={true}
        eventDrop={handleEventDrop}
        eventResize={handleEventResize}
        eventClick={handleEventClick}
        eventDisplay="block"
        eventBackgroundColor="transparent"
        eventContent={renderEventContent}
        eventClassNames={(info) => info.event.extendedProps.classNames}
        dayMaxEvents={0} // Remove the +more link
        height="100%"
        views={{
          dayGridMonth: {
            displayEventTime: true,
            displayEventEnd: true,
            dayMaxEvents: false // Allow unlimited events
          },
          timeGridWeek: {
            displayEventTime: true,
            displayEventEnd: true
          },
          timeGridDay: {
            displayEventTime: true,
            displayEventEnd: true
          }
        }}
      />
    </div>
    <ClassDetailsModal
  visible={detailsModalVisible}
  onClose={() => setDetailsModalVisible(false)}
  classData={selectedClass?.classData}
  teacherData={selectedClass?.teacherData}
/>
    
            </TabPane>
            <TabPane tab="All Class List" key="2">
                      <AllClassList />
                    </TabPane>
                    </Tabs>
            <CustomModal
              isOpen={showModal}
              onClose={() => setShowModal(false)}
              onSubmit={handleSubmitModal}
              reason={changeReason}
              setReason={setChangeReason}
              note={changeNote}
              setNote={setChangeNote}
            />
          </div>
          
  );
  
}

export default withRoleAccess(AllClasses, ['admin', 'superadmin']);